import { cn } from '@mds/shared/lib/utils';

export type SectionHeaderProps = {
  /**
   * The line that names the section — "Send a message".
   *
   * Written in sentence case and uppercased here, so the string stays readable
   * in the source and a screen reader is not handed shouting capitals to spell
   * out letter by letter.
   */
  title: string;
  /** The sentence under it — what the section is for, in one breath. */
  description?: string;
  /**
   * Which heading level this is.
   *
   * A prop because the level depends on where the section sits, and only the
   * page knows that. `h2` for a section of a page; `h3` for a section inside
   * one. It changes the element and nothing else — the type is the same at
   * either level, because this is a label for a band of content rather than a
   * step in a visual hierarchy.
   */
  as?: 'h2' | 'h3';
  className?: string;
};

/**
 * The label above a band of content — a mono line in the brand mint, with an
 * optional sentence under it.
 *
 * Not `PageHeader`, which opens a whole page with a display line and carries a
 * badge, a back link and a row of facts. This names one section of that page.
 *
 * Not `IconCard` with a brand eyebrow either, though the two look alike at a
 * glance. That eyebrow is 11px and a `span` — a label on a block of text, and
 * one of several such blocks in a row. This is body-sized and a real heading,
 * so it lands in the document outline and a screen reader can jump to it. The
 * resemblance is the design being consistent, not the two being one component.
 */
export default function SectionHeader({
  title,
  description,
  as: Heading = 'h2',
  className,
}: SectionHeaderProps) {
  return (
    <header className={cn('flex flex-col items-start', className)}>
      {/* `font-mono` overrides the display face the base layer gives every
          heading. A heading is on Satoshi by virtue of being a heading, which
          is right everywhere except here, where the mono face is the point.
          `font-semibold`: at the regular weight the thin mono strokes in mint
          read too faint to name a section. */}
      <Heading className="font-mono text-md font-semibold tracking-widest text-brand-text uppercase">
        {title}
      </Heading>

      {description && (
        <p className="mt-2 max-w-prose text-base text-pretty text-ink">{description}</p>
      )}
    </header>
  );
}
